import { DotsThree, X } from '@phosphor-icons/react'
import { Popconfirm, Popover } from 'antd'
import {
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactNode,
  useState,
} from 'react'

import { EditableBadgeRawItem } from './EditableBadgeRawItem'

type BadgeListProps = {
  id: string
  content: ReactNode
  onClick?: (tagId: string) => void
} & Partial<
  Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'content' | 'onClick'
  >
>

export type EditableBadgeItemProps = {
  tagId: string
  badgeList: BadgeListProps[]
  title: string
  detailsButtonProps?: Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'children'
  >
  removeButtonProps?: Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'children'
  >
  onConfirmRemove: () => void
  color: string
} & HTMLAttributes<HTMLDivElement>

export const EditableBadgeItem = ({
  tagId,
  title,
  badgeList,
  color,
  detailsButtonProps,
  removeButtonProps,
  onConfirmRemove,
  ...props
}: EditableBadgeItemProps) => {
  const [isOpenPopover, setIsOpenPopover] = useState(false)
  return (
    <EditableBadgeRawItem {...{ color }} {...props}>
      {title}
      <div className="settings-badge absolute right-0 top-0 hidden h-full bg-gray-400">
        <Popover
          classNames={{ body: 'p-2' }}
          content={
            <div className="flex flex-col">
              {badgeList.map(
                ({ id, content, onClick, ...badgeButtonProps }) => (
                  <button
                    className="flex items-center gap-1 rounded-md px-2 py-1 font-medium !outline-none hover:bg-gray-100/70"
                    key={id}
                    onClick={() => {
                      onClick?.(tagId)
                      setIsOpenPopover(false)
                    }}
                    {...badgeButtonProps}
                  >
                    {content}
                  </button>
                )
              )}
            </div>
          }
          onOpenChange={(value) => setIsOpenPopover(value)}
          open={isOpenPopover}
          placement="bottom"
          trigger="click"
        >
          <button
            className="h-full pl-1 text-white/70 !outline-none hover:text-white"
            style={{ background: color }}
            {...detailsButtonProps}
          >
            <DotsThree className="text-xl" weight="bold" />
          </button>
        </Popover>
        <Popconfirm
          cancelButtonProps={{ style: { zoom: 0.75 } }}
          classNames={{ body: '[&_.ant-btn]:!py-3.5' }}
          description="Are you sure you want to remove this tag?"
          icon={false}
          okButtonProps={{ style: { zoom: 0.75 } }}
          onConfirm={onConfirmRemove}
          title={undefined}
        >
          <button
            className="h-full px-2 text-white/70 !outline-none hover:text-white"
            style={{ background: color }}
            {...removeButtonProps}
          >
            <X weight="bold" />
          </button>
        </Popconfirm>
      </div>
    </EditableBadgeRawItem>
  )
}
